import type { ScheduleRoadmapRow } from "../roadmap/buildScheduleRoadmapRows.types";
import type { StripboardTableScene } from "../types";
import { formatBEDateToShortDateString } from "utils/dateFormatters";

export type ScheduleCommentItemKind = "scene" | "daybreak" | "banner";

const DAYBREAK_PREFIX = "daybreak:";
const BANNER_PREFIX = "banner:";

export type ScheduleCommentAnchorTarget = {
  target_id: string;
  scene_id?: number;
  anchor_text: string;
};

export function encodeDaybreakTargetId(daybreakId: number): string {
  return `${DAYBREAK_PREFIX}${daybreakId}`;
}

export function encodeBannerTargetId(bannerId: number): string {
  return `${BANNER_PREFIX}${bannerId}`;
}

export function parseScheduleCommentTargetId(
  targetId: string | null | undefined
): { kind: ScheduleCommentItemKind; id: string } | null {
  if (!targetId) return null;
  if (targetId.startsWith(DAYBREAK_PREFIX)) {
    const id = targetId.slice(DAYBREAK_PREFIX.length);
    return id ? { kind: "daybreak", id } : null;
  }
  if (targetId.startsWith(BANNER_PREFIX)) {
    const id = targetId.slice(BANNER_PREFIX.length);
    return id ? { kind: "banner", id } : null;
  }
  return { kind: "scene", id: targetId };
}

export function buildSceneCommentAnchorText(scene: StripboardTableScene): string {
  const number = scene.scene?.scene_number?.trim();
  const heading = scene.scene?.scene_heading?.trim() ?? scene.int_ext?.trim() ?? "Scene";
  return number ? `Sc. ${number} · ${heading}` : heading;
}

export function buildSceneCommentAnchorFromRow(
  row: ScheduleRoadmapRow
): ScheduleCommentAnchorTarget | null {
  if (row.type !== "scene" || !row.scene) return null;
  return {
    target_id: row.scene.stripboard_id,
    scene_id: row.scene.scene?.scene_id,
    anchor_text: buildSceneCommentAnchorText(row.scene),
  };
}

export function buildShootDayCommentAnchorFromRow(
  row: ScheduleRoadmapRow
): ScheduleCommentAnchorTarget | null {
  if (row.type !== "shoot_day" || !row.daybreak) return null;
  const dateLabel = formatBEDateToShortDateString(row.daybreak.date) || row.daybreak.date;
  const dayLabel = row.label.trim() || "Shoot day";
  return {
    target_id: encodeDaybreakTargetId(row.daybreak.daybreak_id),
    anchor_text: `${dayLabel} · ${dateLabel}`,
  };
}

export function resolveRoadmapRowIdFromTargetId(
  targetId: string,
  rows: ScheduleRoadmapRow[]
): string | null {
  const parsed = parseScheduleCommentTargetId(targetId);
  if (!parsed) return null;

  if (parsed.kind === "scene") {
    const match = rows.find((row) => row.scene?.stripboard_id === parsed.id);
    return match?.id ?? null;
  }

  if (parsed.kind === "daybreak") {
    const daybreakId = Number(parsed.id);
    if (!Number.isFinite(daybreakId)) return null;
    const match = rows.find((row) => row.daybreak?.daybreak_id === daybreakId);
    return match?.id ?? null;
  }

  const bannerId = Number(parsed.id);
  if (!Number.isFinite(bannerId)) return null;
  const match = rows.find((row) => row.banner?.stripoard_banner_id === bannerId);
  return match?.id ?? null;
}

export function collectAncestorRowIdsToExpand(rowId: string, rows: ScheduleRoadmapRow[]): string[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const ancestors: string[] = [];
  let current = byId.get(rowId);
  while (current?.parentId) {
    ancestors.push(current.parentId);
    current = byId.get(current.parentId);
  }
  return ancestors;
}

export function countOpenThreadsByTargetId(
  comments: Array<{
    status: string;
    parent_comment_id?: string | null;
    anchors?: Array<{ target_id?: string | null; feature?: string }>;
  }>
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const comment of comments) {
    if (comment.parent_comment_id) continue;
    if (comment.status !== "open") continue;
    const anchor = comment.anchors?.[0];
    if (!anchor || anchor.feature !== "schedule" || !anchor.target_id) continue;
    counts[anchor.target_id] = (counts[anchor.target_id] ?? 0) + 1;
  }
  return counts;
}

export function scrollToScheduleCommentRow(
  rowId: string,
  scrollContainer?: HTMLElement | null
): void {
  if (typeof document === "undefined") return;
  const rowEl = document.querySelector<HTMLElement>(`[data-schedule-comment-row-id="${rowId}"]`);
  if (!rowEl) return;

  const container =
    scrollContainer ??
    rowEl.closest("[data-roadmap-scroll]")?.parentElement ??
    rowEl.closest(".overflow-y-auto");

  rowEl.scrollIntoView({ block: "center", behavior: "smooth" });

  if (container instanceof HTMLElement) {
    const rowRect = rowEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    if (rowRect.top < containerRect.top || rowRect.bottom > containerRect.bottom) {
      container.scrollTop += rowRect.top - containerRect.top - container.clientHeight / 3;
    }
  }

  rowEl.classList.add("ring-2", "ring-hue-sky/50", "bg-hue-sky/10");
  window.setTimeout(() => {
    rowEl.classList.remove("ring-2", "ring-hue-sky/50", "bg-hue-sky/10");
  }, 1800);
}

export function getScheduleCommentTargetIdForRow(row: ScheduleRoadmapRow): string | null {
  if (row.type === "scene" && row.scene) {
    return row.scene.stripboard_id;
  }
  if (row.type === "shoot_day" && row.daybreak) {
    return encodeDaybreakTargetId(row.daybreak.daybreak_id);
  }
  return null;
}

export function scrollToScheduleCommentAnchor(options: {
  targetId: string;
  rows: ScheduleRoadmapRow[];
  onExpandRows: (rowIds: string[]) => void;
  scrollContainer?: HTMLElement | null;
}): boolean {
  const rowId = resolveRoadmapRowIdFromTargetId(options.targetId, options.rows);
  if (!rowId) return false;

  const ancestors = collectAncestorRowIdsToExpand(rowId, options.rows);
  if (ancestors.length > 0) {
    options.onExpandRows(ancestors);
  }

  window.requestAnimationFrame(() => {
    scrollToScheduleCommentRow(rowId, options.scrollContainer);
  });

  return true;
}
